import React, { useEffect, useRef, useState } from 'react';
import {
  aiHealth, aiGetSuggestions, aiAnalyze, aiApprove, aiReject, aiRespond, aiSummarize, aiKbAsk,
} from '../../services/api';

// Optional custom mascot: drop a file at frontend/public/ai-agent.png to override
// the built-in SVG avatar. If it's absent we fall back to the SVG cleanly.
const AI_IMG = `${process.env.PUBLIC_URL || ''}/ai-agent.png`;

// Premium per-ticket "AI backgrounds": mesh gradients, chosen deterministically
// by ticket id so every ticket gets its own consistent, attractive look.
const THEMES = [
  'radial-gradient(at 18% 20%,#60a5fa 0,transparent 50%),radial-gradient(at 82% 8%,#a78bfa 0,transparent 45%),radial-gradient(at 10% 85%,#22d3ee 0,transparent 45%),radial-gradient(at 85% 80%,#c084fc 0,transparent 50%),linear-gradient(135deg,#1e3a8a,#4c1d95)',
  'radial-gradient(at 20% 15%,#f0abfc 0,transparent 50%),radial-gradient(at 85% 20%,#a855f7 0,transparent 45%),radial-gradient(at 15% 85%,#7c3aed 0,transparent 50%),linear-gradient(135deg,#6b21a8,#831843)',
  'radial-gradient(at 15% 20%,#2dd4bf 0,transparent 50%),radial-gradient(at 85% 15%,#22d3ee 0,transparent 45%),radial-gradient(at 20% 85%,#3b82f6 0,transparent 50%),linear-gradient(135deg,#0f766e,#155e75)',
  'radial-gradient(at 20% 20%,#818cf8 0,transparent 50%),radial-gradient(at 80% 25%,#38bdf8 0,transparent 45%),radial-gradient(at 25% 85%,#c084fc 0,transparent 45%),linear-gradient(135deg,#312e81,#1e3a8a)',
  'radial-gradient(at 18% 18%,#fb7185 0,transparent 50%),radial-gradient(at 82% 20%,#c084fc 0,transparent 45%),radial-gradient(at 20% 82%,#818cf8 0,transparent 50%),linear-gradient(135deg,#9f1239,#5b21b6)',
  'radial-gradient(at 20% 15%,#38bdf8 0,transparent 50%),radial-gradient(at 80% 20%,#818cf8 0,transparent 45%),radial-gradient(at 15% 85%,#22d3ee 0,transparent 50%),linear-gradient(135deg,#0c4a6e,#1e3a8a)',
  'radial-gradient(at 20% 20%,#c084fc 0,transparent 50%),radial-gradient(at 85% 18%,#f0abfc 0,transparent 45%),radial-gradient(at 18% 85%,#8b5cf6 0,transparent 50%),linear-gradient(135deg,#581c87,#3730a3)',
  'radial-gradient(at 18% 20%,#34d399 0,transparent 50%),radial-gradient(at 82% 22%,#22d3ee 0,transparent 45%),radial-gradient(at 20% 85%,#818cf8 0,transparent 50%),linear-gradient(135deg,#065f46,#1e3a8a)',
];
const pickTheme = (id = '') => {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return THEMES[h % THEMES.length];
};

// Self-contained SVG robot avatar — always looks good, no external asset needed.
function AiFace({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden style={{ filter: 'drop-shadow(0 0 8px rgba(34,211,238,.75))' }}>
      <defs>
        <linearGradient id="aiwbody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" /><stop offset="1" stopColor="#cfe6ff" />
        </linearGradient>
        <filter id="aiweyes" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g stroke="#bcd7ff" strokeWidth="2" strokeLinecap="round">
        <line x1="22" y1="14" x2="19" y2="7" /><line x1="42" y1="14" x2="45" y2="7" />
      </g>
      <circle cx="19" cy="6" r="2.4" fill="#22d3ee" /><circle cx="45" cy="6" r="2.4" fill="#22d3ee" />
      <rect x="13" y="14" width="38" height="32" rx="13" fill="url(#aiwbody)" />
      <rect x="26" y="44" width="12" height="10" rx="4" fill="url(#aiwbody)" />
      <rect x="18" y="21" width="28" height="18" rx="9" fill="#0a1226" />
      <g filter="url(#aiweyes)"><circle cx="27" cy="30" r="3.6" fill="#22d3ee" /><circle cx="37" cy="30" r="3.6" fill="#22d3ee" /></g>
      <rect x="9" y="26" width="4" height="9" rx="2" fill="#bcd7ff" /><rect x="51" y="26" width="4" height="9" rx="2" fill="#bcd7ff" />
    </svg>
  );
}

export default function AIAssistantPanel({ ticket, canApprove = false, onApplied }) {
  const [available, setAvailable] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | intro | booting | open | closing
  const [bootLines, setBootLines] = useState([]);
  const [imgOk, setImgOk] = useState(false);

  const [triage, setTriage] = useState(null);
  const [sum, setSum] = useState(null);
  const [resp, setResp] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [kbQ, setKbQ] = useState('');
  const [kbA, setKbA] = useState(null);
  const timers = useRef([]);

  const theme = pickTheme(ticket?.id || ticket?.ticket_number || '');

  useEffect(() => {
    aiHealth().then((r) => setAvailable(!!r.data.configured)).catch(() => setAvailable(false));
    const im = new Image();
    im.onload = () => setImgOk(true);
    im.onerror = () => setImgOk(false);
    im.src = AI_IMG;
  }, []);

  useEffect(() => {
    if (available && ticket?.id) {
      aiGetSuggestions(ticket.id).then((r) => {
        const list = r.data.suggestions || [];
        setTriage(list.find((s) => s.agent === 'triage') || null);
        setSum(list.find((s) => s.agent === 'summary') || null);
        const rs = list.find((s) => s.agent === 'response') || null;
        setResp(rs);
        if (rs?.payload?.suggested_reply) setReplyText(rs.payload.suggested_reply);
      }).catch(() => {});
    }
  }, [available, ticket?.id]);

  useEffect(() => {
    if (phase !== 'booting') return;
    const lines = [
      '> initializing AI core…',
      `> loading ticket ${ticket?.ticket_number || ''} context…`,
      '> connecting to inference engine (groq)…',
      '> retrieving similar tickets & knowledge base…',
      '> triage · response · kb agents online ✓',
    ];
    setBootLines([]);
    let i = 0;
    const step = () => {
      setBootLines((p) => [...p, lines[i]]); i += 1;
      if (i < lines.length) timers.current.push(setTimeout(step, 300));
      else timers.current.push(setTimeout(() => setPhase('open'), 650));
    };
    timers.current.push(setTimeout(step, 250));
    return () => { timers.current.forEach(clearTimeout); timers.current = []; };
  }, [phase, ticket?.ticket_number]);

  if (available === null || available === false) return null;

  const closeIt = () => { setPhase('closing'); timers.current.push(setTimeout(() => setPhase('idle'), 430)); };

  const tp = triage?.payload || {};
  const rp = resp?.payload || {};
  const confPct = tp.confidence != null ? Math.round(tp.confidence * 100) : null;
  const confColor = confPct >= 80 ? '#22c55e' : confPct >= 50 ? '#f59e0b' : '#ef4444';

  const wrap = async (key, fn) => {
    setBusy(key); setErr('');
    try { await fn(); } catch (e) { setErr(e.response?.data?.detail || e.response?.data?.error || 'AI action failed'); } finally { setBusy(''); }
  };
  const runTriage     = () => wrap('triage', async () => setTriage((await aiAnalyze(ticket.id)).data.suggestion));
  const approveTriage = () => wrap('t-appr', async () => { await aiApprove(triage.id, {}); setTriage({ ...triage, status: 'approved' }); onApplied && onApplied(); });
  const rejectTriage  = () => wrap('t-rej',  async () => { await aiReject(triage.id, {}); setTriage({ ...triage, status: 'rejected' }); });
  const runSum     = () => wrap('sum', async () => setSum((await aiSummarize(ticket.id)).data.suggestion));
  const approveSum = () => wrap('s-appr', async () => { await aiApprove(sum.id, {}); setSum({ ...sum, status: 'approved' }); onApplied && onApplied(); });
  const rejectSum  = () => wrap('s-rej', async () => { await aiReject(sum.id, {}); setSum({ ...sum, status: 'rejected' }); });
  const runResp = () => wrap('resp', async () => { const s = (await aiRespond(ticket.id)).data.suggestion; setResp(s); setReplyText(s.payload?.suggested_reply || ''); });
  const approveResp = () => wrap('r-appr', async () => { await aiApprove(resp.id, { text: replyText, internal: internalNote }); setResp({ ...resp, status: 'approved' }); onApplied && onApplied(); });
  const rejectResp  = () => wrap('r-rej', async () => { await aiReject(resp.id, {}); setResp({ ...resp, status: 'rejected' }); });
  const askKb = () => wrap('kb', async () => setKbA((await aiKbAsk(kbQ)).data));

  const Avatar = ({ size }) => (imgOk
    ? <img src={AI_IMG} alt="AI" style={{ width: size, height: size, objectFit: 'contain', filter: 'drop-shadow(0 0 10px rgba(56,189,248,.7))' }} />
    : <AiFace size={size} />);

  return (
    <>
      <style>{AIW_CSS}</style>

      {/* ===== LAUNCHER: small avatar → click → compact intro card ===== */}
      {(phase === 'idle' || phase === 'intro') && (
        <div className="aiw-dock">
          <button
            className="aiw-mini"
            style={{ background: theme }}
            onClick={() => setPhase(phase === 'intro' ? 'idle' : 'intro')}
            aria-label="AI Assistant"
          >
            <span className="aiw-mini-ring" />
            <span className="aiw-float"><Avatar size={44} /></span>
          </button>

          {phase === 'intro' && (
            <div className="aiw-intro aiw-introin" style={{ background: theme }}>
              <div className="aiw-grid" />
              <div className="aiw-intro-inner">
                <div className="aiw-intro-head">
                  <span className="aiw-intro-logo aiw-float"><Avatar size={34} /></span>
                  <div><b>AI Assistant</b><small>Intelligent triage · resolution · KB</small></div>
                </div>
                <p className="aiw-intro-txt">
                  Hi! I can <b>triage</b> this ticket, <b>draft a resolution</b>, and <b>answer from your knowledge base</b> —
                  every suggestion is reviewed by you before anything changes.
                </p>
                <button className="aiw-enter" onClick={() => setPhase('booting')}>Enter Assistant ▸</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== EXPANDED CONSOLE ===== */}
      {(phase === 'booting' || phase === 'open' || phase === 'closing') && (
        <div className={`aiw-card aiw-expanded ${phase === 'closing' ? 'aiw-out' : 'aiw-in'}`} style={{ background: theme }}>
          <div className="aiw-grid" />
          {/* Hero header */}
          <div className="aiw-hero-head">
            <span className="aiw-logo aiw-float"><Avatar size={40} /></span>
            <div className="aiw-hero-txt">
              <h3>AI Assistant</h3>
              <p>{ticket?.ticket_number} · robotic triage console</p>
            </div>
            {phase === 'open' && confPct != null && <span className="aiw-conf" style={{ background: confColor }}>Triage {confPct}%</span>}
            <button className="aiw-close" onClick={closeIt} title="Close" aria-label="Close">▢</button>
          </div>

          {/* Frosted content surface */}
          <div className="aiw-content">
            {phase === 'booting' && (
              <div className="aiw-term">
                {bootLines.map((l, i) => <div key={i} className="aiw-line">{l}</div>)}
                <div className="aiw-line">&gt;&nbsp;<span className="aiw-cursor">▋</span></div>
              </div>
            )}

            {phase === 'open' && (
              <div className="aiw-body">
                {err && <div className="aiw-err">{err}</div>}

                {/* TRIAGE */}
                {!triage ? (
                  <div className="aiw-empty">
                    <p>No AI triage yet for this ticket.</p>
                    <button className="aiw-btn aiw-primary" disabled={!!busy} onClick={runTriage}>{busy === 'triage' ? 'Analyzing…' : '⚡ Analyze with AI'}</button>
                  </div>
                ) : (
                  <div className="aiw-sec">
                    <F label="Suggested Category" v={[tp.category, tp.subcategory].filter(Boolean).join(' / ')} />
                    <div className="aiw-row3"><F label="Priority" v={tp.priority} /><F label="Urgency" v={tp.urgency} /><F label="Impact" v={tp.impact} /></div>
                    <F label="Suggested Technician" v={tp.recommended_technician_name || '— none —'} />
                    {tp.reasoning && <div><div className="aiw-lbl">AI Reasoning</div><div className="aiw-txt">{tp.reasoning}</div></div>}
                    {arr(tp.similar_tickets).length > 0 && <div><div className="aiw-lbl">Similar Tickets</div><ul className="aiw-ul">{tp.similar_tickets.map((s) => <li key={s.id}><b>{s.ticket_number}</b> — {s.title} <em>({s.status})</em></li>)}</ul></div>}
                    <div className="aiw-actions">
                      <span className="aiw-status">Status: <b>{triage.status}</b></span><span className="aiw-sp" />
                      {triage.status === 'pending' && canApprove && (<>
                        <button className="aiw-btn aiw-ok" disabled={!!busy} onClick={approveTriage}>Approve &amp; Apply</button>
                        <button className="aiw-btn" disabled={!!busy} onClick={rejectTriage}>Reject</button>
                      </>)}
                      <button className="aiw-btn" disabled={!!busy} onClick={runTriage}>{busy === 'triage' ? '…' : 'Regenerate'}</button>
                    </div>
                  </div>
                )}

                {/* SUMMARY (Phase 3) */}
                <div className="aiw-sec aiw-divide">
                  <div className="aiw-lbl aiw-neon">Ticket Summary</div>
                  {!sum ? (
                    <button className="aiw-btn aiw-primary" disabled={!!busy} onClick={runSum}>{busy === 'sum' ? 'Summarizing…' : '📝 Generate Summary'}</button>
                  ) : (
                    <>
                      <div className="aiw-txt">{sp(sum).issue_summary}</div>
                      {arr(sp(sum).timeline).length > 0 && <div><div className="aiw-lbl">Timeline</div><ul className="aiw-ul">{sp(sum).timeline.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
                      {arr(sp(sum).actions_taken).length > 0 && <div><div className="aiw-lbl">Actions Taken</div><ul className="aiw-ul">{sp(sum).actions_taken.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
                      <div className="aiw-row3"><F label="Current Status" v={sp(sum).current_status} /></div>
                      {arr(sp(sum).pending_work).length > 0 && <div><div className="aiw-lbl">Pending Work</div><ul className="aiw-ul">{sp(sum).pending_work.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
                      {sp(sum).final_resolution && <div><div className="aiw-lbl">Resolution</div><div className="aiw-txt">{sp(sum).final_resolution}</div></div>}
                      <div className="aiw-actions">
                        <span className="aiw-status">· {sum.status}</span><span className="aiw-sp" />
                        {sum.status === 'pending' && canApprove && (<>
                          <button className="aiw-btn aiw-ok" disabled={!!busy} onClick={approveSum}>Save as Note</button>
                          <button className="aiw-btn" disabled={!!busy} onClick={rejectSum}>Reject</button>
                        </>)}
                        <button className="aiw-btn" disabled={!!busy} onClick={runSum}>{busy === 'sum' ? '…' : 'Regenerate'}</button>
                      </div>
                    </>
                  )}
                </div>

                {/* RESOLUTION */}
                <div className="aiw-sec aiw-divide">
                  <div className="aiw-lbl aiw-neon">Suggested Resolution</div>
                  {!resp ? (
                    <button className="aiw-btn aiw-primary" disabled={!!busy} onClick={runResp}>{busy === 'resp' ? 'Drafting…' : '🛠 Suggest Resolution'}</button>
                  ) : (
                    <>
                      {arr(rp.troubleshooting_steps).length > 0 && <div><div className="aiw-lbl">Troubleshooting Steps</div><ol className="aiw-ul">{rp.troubleshooting_steps.map((s, i) => <li key={i}>{s}</li>)}</ol></div>}
                      <div className="aiw-lbl">Draft Reply {rp.confidence != null && <em>(confidence {Math.round(rp.confidence * 100)}%)</em>}</div>
                      <textarea className="aiw-ta" rows={5} value={replyText} onChange={(e) => setReplyText(e.target.value)} disabled={resp.status !== 'pending'} />
                      {arr(rp.kb_results).length > 0 && <div className="aiw-src">Sources: {rp.kb_results.map((k) => k.title).join(', ')}</div>}
                      <div className="aiw-actions">
                        <label className="aiw-status" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={internalNote} onChange={(e) => setInternalNote(e.target.checked)} disabled={resp.status !== 'pending'} /> internal note</label>
                        <span className="aiw-status">· {resp.status}</span><span className="aiw-sp" />
                        {resp.status === 'pending' && canApprove && (<>
                          <button className="aiw-btn aiw-ok" disabled={!!busy} onClick={approveResp}>Approve &amp; Post</button>
                          <button className="aiw-btn" disabled={!!busy} onClick={rejectResp}>Reject</button>
                        </>)}
                        <button className="aiw-btn" disabled={!!busy} onClick={runResp}>{busy === 'resp' ? '…' : 'Regenerate'}</button>
                      </div>
                    </>
                  )}
                </div>

                {/* KB ASK */}
                <div className="aiw-sec aiw-divide">
                  <div className="aiw-lbl aiw-neon">Ask the Knowledge Base</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="aiw-in" value={kbQ} onChange={(e) => setKbQ(e.target.value)} placeholder="Ask about a known issue…" />
                    <button className="aiw-btn" disabled={!!busy || !kbQ.trim()} onClick={askKb}>{busy === 'kb' ? '…' : 'Ask'}</button>
                  </div>
                  {kbA && (kbA.answer
                    ? <div className="aiw-txt" style={{ marginTop: 8 }}>{kbA.answer}<div className="aiw-src">Sources: {arr(kbA.passages).map((p) => p.title).join(', ')}</div></div>
                    : <div className="aiw-warn">No knowledge base article covers this yet.</div>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const arr = (x) => (Array.isArray(x) ? x : []);
const sp = (s) => (s && s.payload) || {};
function F({ label, v }) { return (<div><div className="aiw-lbl">{label}</div><div className="aiw-val">{v || '—'}</div></div>); }

const AIW_CSS = `
.aiw-card{position:relative;width:100%;max-width:760px;margin:0 auto;border-radius:22px;overflow:hidden;color:#fff;
  box-shadow:0 24px 60px -22px rgba(56,89,255,.55),0 0 0 1px rgba(255,255,255,.14) inset;background-size:cover}
.aiw-grid{position:absolute;inset:0;pointer-events:none;opacity:.25;
  background-image:linear-gradient(rgba(255,255,255,.18) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.18) 1px,transparent 1px);
  background-size:34px 34px;mask-image:radial-gradient(circle at 70% 40%,#000,transparent 75%)}
.aiw-float{display:inline-block;animation:aiw-bob 3.6s ease-in-out infinite}
@keyframes aiw-bob{0%,100%{transform:translateY(-3px)}50%{transform:translateY(4px)}}

.aiw-hero-txt h3{margin:0;font-size:26px;font-weight:800;letter-spacing:-.5px;text-shadow:0 2px 12px rgba(0,0,0,.35)}
.aiw-hero-txt p{margin:2px 0 0;font-size:13.5px;color:rgba(255,255,255,.9);text-shadow:0 1px 8px rgba(0,0,0,.35)}
.aiw-logo{display:grid;place-items:center;width:54px;height:54px;border-radius:16px;
  background:rgba(255,255,255,.16);backdrop-filter:blur(6px);box-shadow:0 0 22px rgba(34,211,238,.4)}

/* Launcher: small avatar + intro popover */
.aiw-dock{position:relative;display:inline-block}
.aiw-mini{position:relative;display:grid;place-items:center;width:64px;height:64px;border-radius:50%;cursor:pointer;
  border:1px solid rgba(255,255,255,.35);box-shadow:0 10px 26px -8px rgba(56,89,255,.7),0 0 0 1px rgba(255,255,255,.15) inset;
  transition:transform .25s cubic-bezier(.2,.8,.2,1),box-shadow .25s ease;background-size:cover}
.aiw-mini:hover{transform:translateY(-3px) scale(1.06);box-shadow:0 16px 34px -10px rgba(56,89,255,.9)}
.aiw-mini-ring{position:absolute;inset:-4px;border-radius:50%;border:2px solid rgba(34,211,238,.6);
  animation:aiw-ping 1.8s ease-out infinite;pointer-events:none}
@keyframes aiw-ping{0%{transform:scale(.9);opacity:.9}70%{transform:scale(1.25);opacity:0}100%{opacity:0}}

.aiw-intro{position:absolute;top:76px;left:0;z-index:40;width:312px;border-radius:18px;overflow:hidden;color:#fff;
  background-size:cover;box-shadow:0 28px 64px -18px rgba(56,89,255,.7),0 0 0 1px rgba(255,255,255,.2) inset}
.aiw-introin{animation:aiw-introin .3s cubic-bezier(.2,.9,.25,1.2) both;transform-origin:top left}
@keyframes aiw-introin{0%{opacity:0;transform:scale(.82) translateY(-10px)}100%{opacity:1;transform:scale(1) translateY(0)}}
.aiw-intro-inner{position:relative;z-index:1;padding:16px}
.aiw-intro-head{display:flex;align-items:center;gap:11px;margin-bottom:11px}
.aiw-intro-logo{display:grid;place-items:center;width:48px;height:48px;border-radius:15px;
  background:rgba(255,255,255,.18);backdrop-filter:blur(6px);box-shadow:0 0 20px rgba(34,211,238,.45)}
.aiw-intro-head b{display:block;font-size:16px;color:#fff;text-shadow:0 2px 10px rgba(0,0,0,.35)}
.aiw-intro-head small{font-size:10.5px;text-transform:uppercase;letter-spacing:.6px;color:rgba(255,255,255,.85)}
.aiw-intro-txt{font-size:13px;line-height:1.55;color:rgba(255,255,255,.94);margin:0 0 14px;text-shadow:0 1px 6px rgba(0,0,0,.3)}
.aiw-intro-txt b{color:#fff}
.aiw-enter{width:100%;padding:11px 16px;border:none;border-radius:12px;cursor:pointer;font-weight:800;font-size:13.5px;color:#0b1020;
  background:rgba(255,255,255,.95);box-shadow:0 8px 20px rgba(0,0,0,.3);transition:transform .2s ease}
.aiw-enter:hover{transform:translateX(3px)}

/* Expanded */
.aiw-expanded{display:flex;flex-direction:column}
.aiw-in{animation:aiw-pop .46s cubic-bezier(.2,.9,.25,1.2) both}
.aiw-out{animation:aiw-popout .42s ease-in both}
@keyframes aiw-pop{0%{opacity:0;transform:scale(.92) translateY(12px)}100%{opacity:1;transform:scale(1) translateY(0)}}
@keyframes aiw-popout{0%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(.94) translateY(8px)}}
.aiw-hero-head{display:flex;align-items:center;gap:12px;padding:16px 18px;position:relative;z-index:1}
.aiw-hero-head .aiw-hero-txt h3{font-size:19px}
.aiw-hero-head .aiw-hero-txt p{font-size:11px;text-transform:uppercase;letter-spacing:.6px}
.aiw-conf{margin-left:auto;color:#04121f;font-weight:800;font-size:12px;border-radius:999px;padding:4px 12px;box-shadow:0 2px 10px rgba(0,0,0,.25)}
.aiw-close{margin-left:10px;width:32px;height:32px;border-radius:9px;cursor:pointer;font-size:14px;color:#fff;
  background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.35);transition:all .2s ease}
.aiw-close:hover{background:#ef4444;border-color:transparent;transform:rotate(90deg)}

.aiw-content{position:relative;z-index:1;margin:0 10px 10px;border-radius:16px;
  background:rgba(8,12,26,.82);backdrop-filter:blur(14px);border:1px solid rgba(255,255,255,.12)}
.aiw-term{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:13px;color:#7CFBD8;padding:22px 20px;min-height:150px}
.aiw-line{white-space:pre-wrap;animation:aiw-typein .25s ease both;text-shadow:0 0 8px rgba(34,211,238,.4)}
@keyframes aiw-typein{from{opacity:0;transform:translateX(-6px)}to{opacity:1;transform:none}}
.aiw-cursor{animation:aiw-blink 1s steps(1) infinite;color:#22d3ee}
@keyframes aiw-blink{50%{opacity:0}}

.aiw-body{padding:16px;max-height:66vh;overflow:auto}
.aiw-sec{display:flex;flex-direction:column;gap:10px;font-size:13px}
.aiw-divide{border-top:1px dashed rgba(148,163,184,.3);margin-top:14px;padding-top:14px}
.aiw-row3{display:flex;gap:18px;flex-wrap:wrap}
.aiw-lbl{font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#7dd3fc;font-weight:700;margin-bottom:3px}
.aiw-neon{color:#c4b5fd;text-shadow:0 0 10px rgba(168,85,247,.5)}
.aiw-val{color:#f1f5f9;font-weight:600}
.aiw-txt{color:#cbd5e1;line-height:1.55}
.aiw-ul{margin:0;padding-left:18px;color:#dbeafe;line-height:1.6}.aiw-ul em{color:#7dd3fc;font-style:normal}
.aiw-src{font-size:12px;color:#94a3b8;margin-top:6px}
.aiw-status{font-size:12px;color:#94a3b8}
.aiw-warn{color:#fbbf24;margin-top:8px}.aiw-err{color:#fca5a5;font-size:13px;margin-bottom:10px}
.aiw-empty p{color:#94a3b8;margin-bottom:10px}
.aiw-actions{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:4px}.aiw-sp{flex:1}
.aiw-ta,.aiw-in{width:100%;border-radius:10px;padding:9px 12px;font-size:13px;font-family:inherit;
  background:rgba(2,6,23,.6);color:#e2e8f0;border:1px solid rgba(148,163,184,.35);outline:none}
.aiw-ta:focus,.aiw-in:focus{border-color:#22d3ee;box-shadow:0 0 0 3px rgba(34,211,238,.25)}
.aiw-btn{cursor:pointer;border-radius:10px;padding:7px 14px;font-size:12.5px;font-weight:700;
  background:rgba(148,163,184,.14);color:#e2e8f0;border:1px solid rgba(148,163,184,.35);transition:all .18s ease}
.aiw-btn:hover:not(:disabled){transform:translateY(-1px);border-color:#22d3ee;box-shadow:0 0 14px rgba(34,211,238,.35)}
.aiw-btn:disabled{opacity:.5;cursor:default}
.aiw-primary{background:linear-gradient(90deg,#22d3ee,#6366f1);border:none;color:#04121f}
.aiw-ok{background:linear-gradient(90deg,#22c55e,#16a34a);border:none;color:#04121f}
@media(max-width:560px){.aiw-hero-txt h3{font-size:22px}.aiw-hero-bottom{flex-direction:column;align-items:flex-start;gap:10px}}
`;
